import React from "react";
import { PropertyDataType } from "../../../backend/constants/datatypes";
import ValueListList from "./valuelists/ValueListList";
import IMfilesValueListItem from "../../../backend/interfaces/mfiles/IMfilesValueListItem";
import LookupList from "./valuelists/LookupList";
import IMFilesPropertyDefinition from "src/backend/interfaces/mfiles/IMFilesPropertyDefinition";
import { isValueList } from "../../../backend/helpers/filteringHelper";
import { BooleanList } from "./valuelists/BooleanList";
import { MFILES_DATE_VALUE_FORMAT } from "../../../backend/constants/common";
import { format } from "date-fns";
import { EmptyList } from "./List";
import { DateInputList } from "./valuelists/DateInputList";
import { TextInputList } from "./valuelists/TextInputList";
import { FilterOperator } from "../../../backend/constants/filterOperators";
import IMfilesValueListDefinition from "../../../backend/interfaces/mfiles/IMFilesValueListDefinition";
import { connect } from "react-redux";
import { iRootState } from "../../../rematch/store";
import { IOwnPropertyDefinition, isOwnPropertyDefinition } from "../../../backend/interfaces/filters/IOwnPropertyDefinition";
import { IFilterValueListItem } from "../../../backend/interfaces/filters/IFilterValueListItem";
import { ArbitraryList } from "./valuelists/ArbitraryList";
import { useTranslation } from "react-i18next";
import { EmptyFilterInput } from "./FiltersCommon";
import { propertyDefinitionsOfFilterPanelsForViewsSelector } from "../../../rematch/selectors/selectors";
import { AxiosInstance } from "axios";



const textBoxPropertyDataTypes = [
  PropertyDataType.Text,
  PropertyDataType.MultilineText,
  PropertyDataType.Integer,
  PropertyDataType.Integer64,
  PropertyDataType.Floating,
  PropertyDataType.FILETIME,
  PropertyDataType.Time,
  PropertyDataType.Timestamp,
  PropertyDataType.ACL,
];

const firstValue = (values: string[]) => (values && values.length > 0 ? values[0] : null);


interface IFilterValuesListProps {
  filterKey            : string;
  visible             : boolean;
  propertyDefinition?  : IMFilesPropertyDefinition | IOwnPropertyDefinition;
  selectedOperator?   : FilterOperator;
  selectedValues      : string[];
  onChange: (selectedValues: string[]) => void;

  // connect
  httpClient               : AxiosInstance;
  valueListDefinitions      : Map<number, IMfilesValueListDefinition>;
  propertyDefinitionsOfViews: IMFilesPropertyDefinition[][];
  valuesResolver: (valuesFunc: (state: iRootState) => unknown[]) => IFilterValueListItem[];
}

const FilterValuesList = (props: IFilterValuesListProps) => {

  const isOwn    = isOwnPropertyDefinition(props.propertyDefinition);
  const dataType = isOwn
    ? (props.propertyDefinition as IOwnPropertyDefinition)?.dataType
    : (props.propertyDefinition as IMFilesPropertyDefinition)?.DataType;

  const { t } = useTranslation();

  const renderInputsAndValues = (): JSX.Element => {

    const isLookup      = dataType === PropertyDataType.Lookup || dataType === PropertyDataType.MultiSelectLookup;
    const isMultiSelect = isLookup && [FilterOperator.OneOf, FilterOperator.NotOneOf].includes(props.selectedOperator);

    if (dataType === PropertyDataType.Date) {
      return (
        <DateInputList
          onChange={(date: Date) => date != null && props.onChange([format(date, MFILES_DATE_VALUE_FORMAT)])}
          value={firstValue(props.selectedValues)}
        />
      );
    } else if (textBoxPropertyDataTypes.includes(dataType)) {
      return (
        <TextInputList
          onChange={(newValue: string) => newValue != null && props.onChange([newValue])}
          value={firstValue(props.selectedValues) || ""}
        />
      );
    } else if (dataType === PropertyDataType.Boolean) {
      return (
        <BooleanList
          value={firstValue(props.selectedValues)}
          onChange={(selectedValue: boolean) => selectedValue != null && props.onChange([selectedValue.toString()])}
        />
      );
    } else if (!isOwn && isLookup && isValueList(props.propertyDefinition as IMFilesPropertyDefinition, props.valueListDefinitions)) {
      return (
        <ValueListList
          isMultiSelect={isMultiSelect}
          selectedValues={props.selectedValues}
          propertyId={(props.propertyDefinition as IMFilesPropertyDefinition)?.ID}
          onChange={(valueListItems: IMfilesValueListItem[]) => props.onChange(valueListItems.map((item) => item.ID.toString()))}
        />
      );
    } else if (!isOwn && isLookup) {

      const propDef = (props.propertyDefinition as IMFilesPropertyDefinition);
      
      return (
        <LookupList
          filterKey={props.filterKey}
          selectedValues={props.selectedValues}
          isMultiSelect={isMultiSelect}
          propertyDefinition={propDef}
          onChange={(selectedIds: string[]) => props.onChange(selectedIds)}
        />
      );
    } else if (isOwn) {
      const ownPropertyDefinition = props.propertyDefinition as IOwnPropertyDefinition;
      const values = props.valuesResolver(ownPropertyDefinition.getValues);
      return (
        <ArbitraryList
          items={values}
          isMultiselect={isMultiSelect}
          onChange={(selectedObjects: IFilterValueListItem[]) => props.onChange( selectedObjects.map((item) => item.value) )}
          selectedItems={props.selectedValues}
        />
      );
    }

    return <EmptyList />;
  };

  return (
    <div>
      {props.visible ? renderInputsAndValues() : <EmptyFilterInput disabled />}
    </div>
  );
};



export default connect((state: iRootState) => ({
  valueListDefinitions: state.common.configuration.valueListDefinitions,
  valuesResolver: (valuesFunc) => valuesFunc(state),
  propertyDefinitionsOfViews: propertyDefinitionsOfFilterPanelsForViewsSelector(state as never),
  httpClient: state.common.httpClient,
}))(FilterValuesList);
